import i18next from "i18next";
import { initSentry, Sentry } from "@/lib/sentry";
import { PortalRequestError } from "@/lib/portalRequest";
import { UploadFormatError } from "@/lib/uploadFormat";

interface PortalErrorContext { operation: string; applicationId?: string }
const reported = new WeakMap<object, string>();

// Bounded diagnostics for signature restoration (Sentry 4B). Never attach the
// original exception/message: it can contain private paths or applicant data.
const signatureFailureCodes = new Map([
  ["Failed to load image", "signature_decode"],
  ["No signature strokes were detected", "signature_empty"],
  ["Unable to process signature image", "signature_canvas"],
  ["Unable to export signature image", "signature_canvas"],
  ["Failed to convert canvas to blob", "signature_encode"],
]);
// pdf-lib messages embed the offending character or offset, so match a prefix.
const pdfFailureCodes: [RegExp, string][] = [
  [/^WinAnsi cannot encode/, "pdf_font_encoding"],
  [/^Failed to parse PDF|No PDF header/, "pdf_parse"],
];
const failureDetail = (raw: string) =>
  signatureFailureCodes.get(raw) ?? pdfFailureCodes.find(([pattern]) => pattern.test(raw))?.[1];

/** Report handled failures too, without sending raw server errors or applicant data. */
export function portalErrorMessage(error: unknown, context: PortalErrorContext): string {
  if (error && typeof error === "object" && reported.has(error)) return reported.get(error)!;
  const request = error instanceof PortalRequestError ? error : undefined;
  const raw = error instanceof Error ? error.message
    : typeof error === "object" && error && "message" in error ? String(error.message) : "";
  const code = typeof error === "object" && error && "code" in error ? String(error.code) : "";
  const kind = request?.kind || (/failed to fetch|load failed|networkerror|network request/i.test(raw) ? "network"
    : /JWT|session expired|not authenticated|no auth token/i.test(raw) || code === "PGRST301" ? "session" : "unknown");
  let key = kind;
  if (request?.status === 403 || code === "42501") key = "permission";
  if (request?.status === 413) key = "fileSize";
  if (request?.status === 415) key = "fileType";
  if (request?.status === 404) key = "missing";
  if (request?.status === 429) key = "busy";
  // Rejected in the browser before upload: a known image format that could not
  // be converted gets conversion advice, anything else the format list.
  const upload = error instanceof UploadFormatError ? error.info : request?.fileInfo;
  if (error instanceof UploadFormatError) key = upload!.format === "unknown" ? "fileType" : "fileConvert";
  const messages: Record<string, string> = {
    network: "The connection was interrupted. Keep this page open, check your connection and try again. If it continues, contact support with the reference below.",
    timeout: "This is taking longer than expected. Keep this page open and try again. If it continues, contact support with the reference below.",
    session: "Your session could not be verified. Sign in again in another tab, then return here and retry.",
    permission: "You do not have permission to save or open this document. Contact support with the reference below.",
    fileSize: "This file is too large. Choose a file smaller than 10 MB and try again.",
    fileType: "This file format is not supported. Please use a PDF, JPEG, PNG, GIF or WebP file.",
    fileConvert: "This image could not be converted in your browser. Please save it as a JPEG or PNG (or take a screenshot of it) and try again.",
    missing: "The requested document could not be found. Retry, or contact support with the reference below.",
    busy: "The service is busy. Please wait a moment and try again.",
    http: "The service could not complete this request. Please retry. If it continues, contact support with the reference below.",
    response: "The service returned an incomplete response. Please retry. If it continues, contact support with the reference below.",
    unknown: "We could not complete this step. Please retry. If it continues, contact support with the reference below.",
  };
  // An early failure can occur before the idle-time initialization in main.tsx.
  if (!Sentry.getClient()) initSentry();
  const eventId = Sentry.captureException(new Error(`Portal ${context.operation} failed (${key})`), {
    tags: {
      area: "application-portal", operation: context.operation, failure_kind: key,
      failure_detail: failureDetail(raw),
      request_phase: request?.phase,
      file_format: upload?.format,
      file_extension: upload?.extension,
    },
    extra: {
      application_id: context.applicationId,
      endpoint: request?.endpoint,
      http_status: request?.status,
      request_id: request?.requestId,
      online: navigator.onLine,
      visibility: document.visibilityState,
      file_mime: upload?.mime,
      file_converted: upload?.converted,
      // Only stable database error codes; raw messages can contain personal data.
      database_code: /^[A-Z0-9]{5,12}$/.test(code) ? code : undefined,
    },
  });
  const message = `${i18next.t(`portal:errors.${key}`, { defaultValue: messages[key] || messages.unknown })} ${i18next.t("portal:errors.reference", { defaultValue: "Reference: {{id}}", id: eventId })}`;
  if (error && typeof error === "object") reported.set(error, message);
  return message;
}
